import { useEffect, useState } from 'react';

let registerAppServiceWorker: ((reload?: boolean) => Promise<void>) | null = null;
const SERVICE_WORKER_TIMEOUT_MS = 10000;
const SW_RELOAD_GUARD_KEY = 'sw:lastReloadAt';
const SW_RELOAD_COOLDOWN_MS = 30_000;
let isApplyingServiceWorkerUpdate = false;
let isReloadingForServiceWorkerUpdate = false;

function recentlyReloadedForSw(): boolean {
  try {
    const raw = sessionStorage.getItem(SW_RELOAD_GUARD_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    if (!Number.isFinite(ts)) return false;
    return Date.now() - ts < SW_RELOAD_COOLDOWN_MS;
  } catch {
    return false;
  }
}

function markSwReload() {
  try { sessionStorage.setItem(SW_RELOAD_GUARD_KEY, String(Date.now())); } catch {}
}

export async function ensureAppServiceWorkerRegistered(): Promise<ServiceWorkerRegistration | undefined> {
  if (!('serviceWorker' in navigator)) {
    console.log('Service Worker not supported');
    return undefined;
  }

  // Skip SW in iframe/preview contexts to avoid noisy ready-timeouts in Sentry
  const isInIframe = (() => { try { return window.self !== window.top; } catch { return true; } })();
  const isPreviewHost =
    window.location.hostname.includes('id-preview--') ||
    window.location.hostname.includes('lovableproject.com');
  if (isInIframe || isPreviewHost) {
    return undefined;
  }

  try {
    if (!registerAppServiceWorker) {
      const { registerSW } = await import('virtual:pwa-register');
      registerAppServiceWorker = registerSW({
        immediate: true,
        onRegisteredSW(swUrl, registration) {
          console.log('App service worker registered', { swUrl, scope: registration?.scope });
        },
        onRegisterError(error) {
          console.error('App service worker registration failed', error);
        },
        onNeedRefresh() {
          if (isApplyingServiceWorkerUpdate) return;
          if (recentlyReloadedForSw()) return;
          isApplyingServiceWorkerUpdate = true;
          markSwReload();
          void registerAppServiceWorker?.(true);
        },
        onOfflineReady() {
          console.log('App ready to work offline');
        },
      });
    }

    registerAppServiceWorker();
  } catch (e) {
    console.warn('SW registerSW failed, will try navigator.serviceWorker.ready anyway', e);
  }

  try {
    // Race ready vs timeout; resolve (not reject) on timeout so Sentry stays quiet.
    // SW may still be installing — fall back to getRegistration which returns whatever exists.
    const registration = await new Promise<ServiceWorkerRegistration | undefined>((resolve) => {
      const timer = window.setTimeout(async () => {
        const fallback = await navigator.serviceWorker.getRegistration('/').catch(() => undefined);
        resolve(fallback);
      }, SERVICE_WORKER_TIMEOUT_MS);
      navigator.serviceWorker.ready.then((reg) => {
        window.clearTimeout(timer);
        resolve(reg);
      }).catch(async () => {
        window.clearTimeout(timer);
        const fallback = await navigator.serviceWorker.getRegistration('/').catch(() => undefined);
        resolve(fallback);
      });
    });
    return registration;
  } catch {
    return undefined;
  }
}

export function useServiceWorker() {
  useEffect(() => {
    const timer = window.setTimeout(() => {
      void ensureAppServiceWorkerRegistered();
    }, 2_500);

    if (!('serviceWorker' in navigator)) {
      window.clearTimeout(timer);
      return;
    }

    const handleControllerChange = () => {
      console.log('New service worker activated');
      if (isReloadingForServiceWorkerUpdate) return;
      if (recentlyReloadedForSw()) {
        isReloadingForServiceWorkerUpdate = true;
        return;
      }
      isReloadingForServiceWorkerUpdate = true;
      markSwReload();
      const active = document.activeElement as HTMLElement | null;
      const userIsTyping = !!active && (
        active.tagName === 'INPUT'
        || active.tagName === 'TEXTAREA'
        || active.isContentEditable
      );
      window.setTimeout(() => window.location.reload(), userIsTyping ? 3_000 : 250);
    };

    navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);

    return () => {
      window.clearTimeout(timer);
      navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
    };
  }, []);
}

export function useOnlineStatus() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return isOnline;
}
